/**
 * Мелкие предпочтения, переживающие перезагрузку вкладки.
 *
 * ⚠️ ЗДЕСЬ НЕТ И НЕ ДОЛЖНО БЫТЬ НИЧЕГО СЕКРЕТНОГО. Токены живут в
 * `lib/token-storage.ts` со своими правилами (access — только в памяти); сюда
 * попадает то, потеря или подмена чего не имеет последствий: какой экран
 * открывать после входа и какое мероприятие сканировали в прошлый раз.
 *
 * Всё чтение защищено `try/catch`: в приватном режиме Safari и при запрете
 * хранилища в настройках сайта обращение к `localStorage` **бросает**, а не
 * возвращает `null`. Непойманное — это белый экран на старте приложения ради
 * запомненной вкладки.
 */

/** Куда человек хочет попадать после входа. */
export type LoginDestination = 'admin' | 'scanner'

const DESTINATION_KEY = 'acr.login_destination'
const LAST_EVENT_KEY = 'acr.scanner.last_event'
const SOUND_KEY = 'acr.scanner.sound'

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* хранилище недоступно — предпочтение просто не переживёт перезагрузку */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* см. write */
  }
}

/**
 * Куда уводить после входа.
 *
 * Запоминается потому, что выбор почти всегда один и тот же: оператор на входе
 * всю смену открывает сканер, администратор — панель. Спрашивать одно и то же
 * при каждом входе — это лишний выбор в момент, когда человек уже решил, зачем
 * пришёл.
 *
 * ⚠️ Неизвестное значение трактуется как «админка», а не как ошибка: ключ мог
 * остаться от другой версии приложения, и падать из-за него нельзя.
 */
export const loginDestination = {
  get(): LoginDestination {
    return read(DESTINATION_KEY) === 'scanner' ? 'scanner' : 'admin'
  },
  set(value: LoginDestination): void {
    write(DESTINATION_KEY, value)
  },
}

/**
 * Мероприятие, которое сканировали в прошлый раз.
 *
 * ⚠️ Это подсказка, а не состояние. Применять его можно, только убедившись, что
 * мероприятие всё ещё в списке доступных: состав меняют, доступ снимают, и
 * переход на недоступное мероприятие даёт экран «недоступно» вместо работы.
 */
export const lastScannerEvent = {
  get(): string | null {
    return read(LAST_EVENT_KEY)
  },
  set(eventId: string): void {
    write(LAST_EVENT_KEY, eventId)
  },
  clear(): void {
    remove(LAST_EVENT_KEY)
  },
}

/**
 * Звуковой отклик сканера. По умолчанию **включён**: сигнал — единственное, что
 * сообщает исход, не требуя взгляда на экран, и человек, впервые открывший
 * сканер, должен его услышать, а не искать в настройках.
 *
 * Выключатель нужен потому, что зал бывает со своей музыкой и своим ведущим:
 * там сигнал не слышно, и он только мешает соседям. Вибрация при этом остаётся
 * — это независимый канал.
 *
 * ⚠️ Хранится строкой `'off'`, а не булевым `'false'`: значение по умолчанию
 * «включено», и любое неизвестное содержимое ключа (другая версия, мусор)
 * обязано означать «включено», а не «молчим».
 */
export const soundEnabled = {
  get(): boolean {
    return read(SOUND_KEY) !== 'off'
  },
  set(value: boolean): void {
    write(SOUND_KEY, value ? 'on' : 'off')
  },
}
